import { useEffect, useMemo, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { MessageBubble } from './MessageBubble';
import type { StreamMessage } from './messagesApi';

export function MessageStream({ messages, loading = false }: { messages: StreamMessage[]; loading?: boolean }) {
  const parentRef = useRef<HTMLDivElement | null>(null);
  const shouldStickRef = useRef(true);
  const count = messages.length;
  const rowVirtualizer = useVirtualizer({
    count,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 76,
    overscan: 8,
  });
  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();
  const key = useMemo(() => messages.map((m) => m.id).join('|'), [messages]);

  useEffect(() => {
    if (shouldStickRef.current && count > 0) {
      rowVirtualizer.scrollToIndex(count - 1, { align: 'end' });
    }
  }, [count, key, rowVirtualizer]);

  if (loading) {
    return <div className="empty-state" data-testid="message-loading"><div className="spinner" /> <span className="muted">Loading messages...</span></div>;
  }
  if (messages.length === 0) {
    return <div className="empty-state" data-testid="message-empty"><span className="muted">No messages yet. Start the conversation below.</span></div>;
  }

  return (
    <div
      ref={parentRef}
      data-testid="message-stream"
      onScroll={() => {
        const el = parentRef.current;
        if (!el) return;
        shouldStickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
      }}
      style={{ overflow: 'auto', flex: 1, minHeight: 0 }}
    >
      <div style={{ height: totalSize, width: '100%', position: 'relative' }}>
        {virtualItems.map((virtualRow) => {
          const message = messages[virtualRow.index];
          return (
            <div
              key={message.id}
              data-index={virtualRow.index}
              ref={rowVirtualizer.measureElement}
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', transform: `translateY(${virtualRow.start}px)` }}
            >
              <MessageBubble message={message} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
